/**
 * Pokes data layer (Issue #11).
 *
 * Mirrors the legacy CRA Topbar `NotificationBar` (REACTJS/src/app/components/
 * Layout/Layout1/Layout1Topbar.jsx) which fetches received pokes from
 * `GET user/pokes/{userId}` and exposes two button actions per row:
 *
 *   - Poke back  →  POST user/action/poke/{pokerId}
 *   - Cancel     →  POST user/action/cancelpoke/{pokerId}
 *
 * The list returns the *pokers* — each item is a user record with `ID`,
 * `firstname`, `lastname`, `username`, `image`, `extra` (gender/age/location).
 * The signed-in user pokes-back / cancels by passing the poker's user ID.
 *
 * `usePokes` is disabled until an auth token is present so unauthenticated
 * visitors never hit `/user/pokes/0`. On mutation success the list cache is
 * invalidated (the `remove: true` flag on the legacy buttons removed the row
 * from the panel; we re-fetch instead to keep things simple and correct).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuth } from '@/pages/auth';
import type { User } from '@/types/api';

/**
 * A single received-poke row — the *poker's* user record. The legacy panel
 * keys actions off `item.ID`, so we follow that exact convention.
 */
export type PokeItem = User;

/** Payload returned by `GET user/pokes/{id}`. */
export interface PokesResult {
  total?: number;
  list: PokeItem[];
  next?: boolean | number;
}

/**
 * Received pokes for the signed-in user. Disabled (no request) when
 * unauthenticated; gated on the auth token the same way `useCounts` is.
 */
export function usePokes() {
  const token = useAuth((s) => s.token);
  const me = useAuth((s) => s.user);
  const userId = me?.ID;

  return useQuery({
    queryKey: queryKeys.pokes.list(userId ?? 0),
    enabled: Boolean(token) && Boolean(userId),
    staleTime: 30_000,
    queryFn: () => api.get<PokesResult>(`user/pokes/${userId}`),
  });
}

/**
 * Send a poke (or poke-back) to a target user.
 *
 * Endpoint: `POST user/action/poke/{targetUserId}` — matches the legacy
 * `useUserAction()` mutation and the legacy Topbar button URL pattern.
 */
export function usePokeMutation() {
  const qc = useQueryClient();
  const me = useAuth((s) => s.user);

  return useMutation({
    mutationFn: (targetUserId: number) =>
      api.post<{ msg?: string } | string | null>(
        `user/action/poke/${targetUserId}`,
      ),
    onSuccess: () => {
      // Re-fetch the received-pokes list (poke-back removes the row server-side)
      // and the counts badge so the topbar/sidebar update in lock-step.
      qc.invalidateQueries({ queryKey: queryKeys.pokes.list(me?.ID ?? 0) });
      qc.invalidateQueries({ queryKey: queryKeys.counts.me() });
    },
  });
}

/**
 * Cancel / dismiss a received poke (the trash button in the legacy panel).
 *
 * Endpoint: `POST user/action/cancelpoke/{pokerUserId}`.
 */
export function useCancelPokeMutation() {
  const qc = useQueryClient();
  const me = useAuth((s) => s.user);

  return useMutation({
    mutationFn: (pokerUserId: number) =>
      api.post<{ msg?: string } | string | null>(
        `user/action/cancelpoke/${pokerUserId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.pokes.list(me?.ID ?? 0) });
      qc.invalidateQueries({ queryKey: queryKeys.counts.me() });
    },
  });
}
